// Export the product list to CSV or XLSX.
//
// The CSV it produces is the same shape the importer reads, so the round-trip
// is literal: export, edit a hundred prices in Excel, import back. No PDF —
// a product list is worked on in a spreadsheet, not read on paper.

import { useState } from 'react';
import { ChevronDown, FileText, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { downloadBlob } from '@/lib/api/invoice';
import {
  buildProductCsv,
  buildProductXlsx,
  filterProducts,
  type ProductExportFilters,
} from '@/lib/api/productsExport';
import { useCategories } from '@/hooks/useCategories';
import { cn } from '@/lib/format';
import type { Product } from '@/types/domain';

export type ProductExportFormat = 'csv' | 'xlsx';

export interface ProductExportMenuProps {
  products: Product[];
  filters: ProductExportFilters;
  disabled?: boolean;
}

export function ProductExportMenu({ products, filters, disabled }: ProductExportMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [busy, setBusy] = useState<ProductExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);

  // All categories, not just active ones: a product may still be filed under a
  // deactivated category, and exporting a blank there would silently drop it on
  // the way back in.
  const { data: categories } = useCategories();

  const count = filterProducts(products, filters).length;
  const stamp = new Date().toISOString().slice(0, 10);

  const run = async (format: ProductExportFormat) => {
    setBusy(format);
    setError(null);
    try {
      const cats = categories ?? [];
      if (format === 'csv') {
        const csv = buildProductCsv(products, cats, filters);
        // Same BOM as downloadCsv writes, so Excel opens it in UTF-8 — and
        // parseCsv strips it again on the way back.
        downloadBlob(
          new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }),
          `produk-${stamp}.csv`,
        );
      } else {
        downloadBlob(await buildProductXlsx(products, cats, filters), `produk-${stamp}.xlsx`);
      }
      setIsOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export gagal');
    } finally {
      setBusy(null);
    }
  };

  const options: { format: ProductExportFormat; label: string; description: string; icon: typeof FileText }[] = [
    {
      format: 'csv',
      label: 'CSV',
      description: 'Bisa diedit lalu di-import kembali',
      icon: FileText,
    },
    {
      format: 'xlsx',
      label: 'Excel (XLSX)',
      description: 'Kolom rapi, header terkunci',
      icon: FileSpreadsheet,
    },
  ];

  return (
    <div className="relative">
      <Button
        variant="secondary"
        disabled={disabled || count === 0}
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        Export
        <ChevronDown size={14} className="ml-1" />
      </Button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} aria-hidden />
          <div
            role="menu"
            className="absolute right-0 mt-1 z-20 w-64 rounded border border-line bg-surface shadow-lg py-1"
          >
            <p className="px-3 py-2 text-caption text-ink-subtle border-b border-line">
              {count} produk sesuai filter saat ini
            </p>
            {options.map((o) => (
              <button
                key={o.format}
                type="button"
                role="menuitem"
                disabled={busy !== null}
                onClick={() => run(o.format)}
                className={cn(
                  'w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-canvas',
                  'focus:bg-canvas focus:outline-none disabled:opacity-60',
                )}
              >
                {busy === o.format ? (
                  <Loader2 size={14} className="mt-0.5 animate-spin text-ink-subtle" />
                ) : (
                  <o.icon size={14} className="mt-0.5 text-ink-subtle" />
                )}
                <span>
                  <span className="block text-small text-ink">{o.label}</span>
                  <span className="block text-caption text-ink-subtle">{o.description}</span>
                </span>
              </button>
            ))}
            {error && <p className="px-3 py-2 text-caption text-danger">{error}</p>}
          </div>
        </>
      )}
    </div>
  );
}
